import React, { ReactNode } from "react";
import styled from "styled-components";
import { LanguageNameSpaces, PieChartDataPoint } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import AllocationDonut from "common/NewLook/AllocationDonut.tsx";
import Panel from "common/NewLook/Panel.tsx";

interface NewLookAllocationProps {
  data: PieChartDataPoint[];
  total: ReactNode;
  loading?: boolean;
  tooltipElementId?: string;
  /** Shown on the right of the title, e.g. the export button on mobile. */
  action?: ReactNode;
  /** Smaller donut for phones. */
  compact?: boolean;
  /** Just the donut and legend: the overview panel supplies the surface and title. */
  bare?: boolean;
}

/**
 * Allocation donut with a legend of the largest positions. On desktop it is a
 * titled panel (title in the header, like every other panel); on phones the
 * donut keeps its collapsible summary card.
 */
const NewLookAllocation = ({
  data,
  total,
  loading,
  tooltipElementId = "trades-chart",
  action,
  compact,
  bare,
}: NewLookAllocationProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const donut = (
    <AllocationDonut
      title={compact ? t("charts.amount_allocation") : undefined}
      action={compact ? action : undefined}
      data={data}
      total={total}
      loading={loading}
      chartId={tooltipElementId}
      compact={compact}
      bare={!compact}
      emptyTitle={tCommon("empty.allocation_no_trades")}
      emptyDescription={tCommon("empty.allocation_no_trades_description")}
    />
  );
  if (compact || bare) return donut;
  return (
    <AllocationPanel
      title={t("charts.amount_allocation")}
      actions={action}
    >
      {donut}
    </AllocationPanel>
  );
};

export default React.memo(NewLookAllocation);

// The donut brings 16px of space above itself; the body centres it in a panel
// stretched to its row.
const AllocationPanel = styled(Panel)`
  height: 100%;
  > div:last-child {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 24px 20px;
  }
`;
